class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);

/* ==========================================================================
   Open header menus on hover (desktop only)
   Added customization: submenus reveal on mouse hover instead of requiring a
   click. Touch devices and mobile widths keep the default click behavior.
   ========================================================================== */
(function () {
  const HOVER_MEDIA = '(min-width: 990px) and (hover: hover) and (pointer: fine)';
  const CLOSE_DELAY = 150;

  function canHover() {
    return window.matchMedia(HOVER_MEDIA).matches;
  }

  function openDetails(details) {
    const summary = details.querySelector('summary');
    details.setAttribute('open', '');
    if (summary) summary.setAttribute('aria-expanded', 'true');
  }

  function closeDetails(details) {
    const summary = details.querySelector('summary');
    details.removeAttribute('open');
    if (summary) summary.setAttribute('aria-expanded', 'false');
  }

  function bindHover(details, scope) {
    if (!details || details.dataset.hoverBound === 'true') return;

    const summary = details.querySelector('summary');
    if (!summary) return;

    details.dataset.hoverBound = 'true';
    const panel = summary.nextElementSibling;
    let closeTimer;

    const open = () => {
      if (!canHover()) return;
      clearTimeout(closeTimer);

      // Close any sibling menu that is already open.
      (scope || document).querySelectorAll('details[open][data-hover-bound="true"]').forEach((other) => {
        if (other !== details && !other.contains(details) && !details.contains(other)) closeDetails(other);
      });

      openDetails(details);
    };

    const scheduleClose = () => {
      if (!canHover()) return;
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => closeDetails(details), CLOSE_DELAY);
    };

    details.addEventListener('mouseenter', open);
    details.addEventListener('mouseleave', scheduleClose);
    summary.addEventListener('mouseenter', open);

    if (panel) {
      panel.addEventListener('mouseenter', () => clearTimeout(closeTimer));
      panel.addEventListener('mouseleave', scheduleClose);
    }

    // Stop a click from immediately closing a menu that hover just opened.
    summary.addEventListener('click', (event) => {
      if (canHover() && details.hasAttribute('open')) event.preventDefault();
    });
  }

  function initHoverMenus(root) {
    const container = root || document;

    // Top level menus (standard dropdown and mega menu both use <header-menu>).
    container.querySelectorAll('header-menu > details').forEach((details) => {
      bindHover(details, container.querySelector('.header__inline-menu') || document);
    });

    // Nested (third level) submenus inside a dropdown panel.
    container.querySelectorAll('.header__submenu details').forEach((details) => {
      bindHover(details, details.closest('.header__submenu') || document);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initHoverMenus());
  } else {
    initHoverMenus();
  }

  // Re-bind after the theme editor reloads the header section.
  document.addEventListener('shopify:section:load', (event) => initHoverMenus(event.target));
})();
